import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { MuiBox, CursorMuiBox } from 'theme/material-ui';
import Svg from 'components/UI/Svg/Svg';
import Typography from 'components/UI/Typography';
import Divider from 'components/UI/Divider';
import Spacer from 'components/UI/Layout/Spacer';
import StyledButton from 'components/UI/Button/StyledButton';
import { Color } from 'modules/styles/colors';
import { IconSize, FontSize, FontWeight } from 'modules/styles/variables';
import {
  PreRegistrationFacilitySelector,
  PreRegistrationEncounterInfoSelector,
  PreRegistrationVisitDetailSelector
} from 'store/preRegistration/selectors';
import {
  currentLocationPathNameSelector,
  previousLocationPathNameSelector
} from 'store/router/selectors';
import analyticsService, { AmplitudeEventData, AnalyticsEvent } from 'services/AnalyticsService';
import { RootState } from 'store/types';
import { connect, useDispatch } from 'react-redux';
import { FacilityLocation, VisitDetail } from 'store/preRegistration/types';
import * as actions from 'store/preRegistration/actions';
import { Alert } from 'components/Alert';
import moment from 'moment';
import LinkingServices from 'services/LinkingServices';
import { PreRegConfirmModal } from '../common';

interface CompleteProps {
  facilityLocation: FacilityLocation | undefined;
  referringUrl: string;
  currentUrl: string;
  visitDetail: VisitDetail;
  complete?: boolean;
}

export function CompleteModal({
  facilityLocation,
  currentUrl,
  referringUrl,
  visitDetail,
  complete
}: CompleteProps) {
  const history = useHistory();
  const dispatch = useDispatch();

  const [hasRequestedExit, setRequestExit] = useState(false);

  const onClose = () => {
    setRequestExit(false);
    dispatch({ type: actions.PRE_REGISTRATION_RESET });
    window.sessionStorage.setItem('PRE_REG', '');
    Alert.alert('You have been logged out.', 'Thank you.', [
      {
        text: 'OK',
        onPress: () => {
          history.push('/pre-reg/logged-out');
        }
      }
    ]);
  };

  const requestExit = () => {
    setRequestExit(true);
  };
  const stayHere = () => {
    setRequestExit(false);
  };

  const onButtonPress = () => {
    history.push(`/pre-reg/checklist`);
  };

  const onViewMapPress = () => {
    const amplitudeEventData: AmplitudeEventData = {
      currentUrl,
      referringUrl,
      /* eslint-disable-next-line @typescript-eslint/camelcase */
      form_location: facilityLocation?.name || ''
    };
    analyticsService.logEvent(AnalyticsEvent.HospitalFormViewBuildingMap, amplitudeEventData);
  };

  const getFacilityLocation = () => {
    if (!facilityLocation) {
      return null;
    }
    const { name, address } = facilityLocation;
    const service = visitDetail.serviceDescription ? ` - ${visitDetail.serviceDescription}` : '';
    return (
      <>
        <MuiBox>
          <Typography fontSize={FontSize.large}>{`${name}${service}`}</Typography>
        </MuiBox>
        {address ? (
          <MuiBox>
            <Typography fontSize={FontSize.large}>{facilityLocation?.address.street1}</Typography>
          </MuiBox>
        ) : null}
        {address ? (
          <MuiBox>
            <Typography fontSize={FontSize.large}>
              {address.city}, {address.state} {address.postalCode}
            </Typography>
          </MuiBox>
        ) : null}
      </>
    );
  };

  const showOnlyDate = moment(visitDetail.startDate).format('MMMM Do, YYYY');

  const numberToCall = `1${facilityLocation?.phone?.areaCode}${facilityLocation?.phone?.localNumber}`.toString();
  const callLocationNumber = () => {
    LinkingServices.callPhoneNumber(numberToCall);
  };

  return (
    <MuiBox pt={7} pb={3} height="100%">
      <MuiBox display="flex" flexDirection="column" height="100%">
        <MuiBox p={8} pb={5} display="flex" flexDirection="column" alignItems="center">
          <Svg name="check_circle_outline" set="material" size={70} color={Color.green} />
          <Spacer spacing="large" />
          <Typography textAlign="center" fontSize={FontSize.heading} fontWeight={FontWeight.bold}>
            Success!
          </Typography>
          <Spacer spacing="large" />
          <Typography
            textAlign="center"
            fontSize={FontSize.heading}
            data-testid="pre-reg-complete-modal-heading-text"
          >
            {complete ? (
              <>
                You've already completed your pre-visit forms. You are all set for your appointment.
              </>
            ) : (
              <>Thank you for submitting your pre-visit forms through My Health+.</>
            )}
          </Typography>
          <Spacer spacing="small" />
          <Typography textAlign="center" fontSize={FontSize.heading}>
            As you arrive for your visit you will complete your check-in.
          </Typography>
        </MuiBox>
        <Divider />
        <MuiBox px={3} py={4}>
          <MuiBox display="flex" alignItems="center">
            <MuiBox mr={2}>
              <Svg name="CalendarFlat" size={IconSize.base} />
            </MuiBox>
            <Typography fontSize={FontSize.large}>{showOnlyDate}</Typography>
          </MuiBox>
          <Spacer spacing="small" />
        </MuiBox>
        {!complete ? (
          <>
            <Divider />
            <MuiBox
              px={3}
              py={4}
              flexGrow={1}
              display="flex"
              flexDirection="column"
              justifyContent="flex-end"
            >
              <StyledButton onPress={onButtonPress} textStyle={{ fontSize: FontSize.base }}>
                Back to checklist
              </StyledButton>
            </MuiBox>
          </>
        ) : null}
      </MuiBox>
      <MuiBox position="absolute" top="10px" right="10px">
        <Svg name="close" set="material" size={IconSize.large} onClick={requestExit} />
      </MuiBox>
      <PreRegConfirmModal
        icon="ExclamationCircle"
        isOpen={hasRequestedExit}
        title="Are you sure you want to logout?"
        onConfirm={onClose}
        onCancel={stayHere}
        confirmLabel="Logout"
        cancelLabel="Keep going"
        description="Any unsaved changes will be lost."
        backdropColor="#213245"
      />
    </MuiBox>
  );
}

const mapStateToProps = (state: RootState) => {
  return {
    encounter: PreRegistrationEncounterInfoSelector(state),
    facilityLocation: PreRegistrationFacilitySelector(state),
    currentUrl: currentLocationPathNameSelector(state),
    referringUrl: previousLocationPathNameSelector(state),
    visitDetail: PreRegistrationVisitDetailSelector(state)
  };
};

export default connect(mapStateToProps)(CompleteModal);
